import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Box,
  TablePagination,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  InputAdornment,
} from "@mui/material";
import { ArrowUpward, ArrowDownward, Search, Close, MoreVert } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useNavigate } from "react-router-dom";
import { IStore } from "../../store/IStore";
import { thunkGetAllCampaignsExperimental } from "../../store/actions/CampaignExperimentalActions";
import { thunkCreateSuccessNotification, thunkCreateErrorNotification } from "../../store/actions/NotificationActions";
import { Api } from "../../api/Api";
import {
  CampaignExperimentalDto,
  CampaignExperimentalStatus,
  CampaignExperimentalType,
} from "../../store/models/campaignExperimental/CampaignExperimentalDto";
import { UrlPathCampaignExperimentalEdit } from "../../api/url";

// Constants
const PAGINATION_CONSTANTS = {
  DEFAULT_ROWS_PER_PAGE: 10,
  ROWS_PER_PAGE_OPTIONS: [10, 20, 50, 100],
  API_PAGE_OFFSET: 1,
  TABLE_COLUMN_COUNT: 5,
};

type ChipColor = "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";

// Utility functions
const formatCampaignDate = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

const createChip = (label: string, color: ChipColor, fallback: string = "-"): React.ReactNode => {
  if (!label) {
    return <Typography variant="body2">{fallback}</Typography>;
  }
  return <Chip label={label} color={color} size="small" variant="outlined" />;
};

interface SortableHeaderProps {
  field: string;
  children: React.ReactNode;
  sx?: React.CSSProperties;
  align?: "left" | "center" | "right";
  onSort: (field: string) => void;
  sortBy: string;
  sortDirection: "asc" | "desc";
}

const SortableHeader: React.FC<SortableHeaderProps> = ({
  field,
  children,
  sx,
  align,
  onSort,
  sortBy,
  sortDirection,
}) => (
  <TableCell onClick={() => onSort(field)} sx={{ cursor: "pointer", userSelect: "none", ...sx }} align={align}>
    <Box display="flex" alignItems="center" justifyContent={align === "center" ? "center" : "flex-start"}>
      <Typography variant="subtitle2" fontWeight="bold">
        {children}
      </Typography>
      {sortBy === field &&
        (sortDirection === "asc" ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />)}
    </Box>
  </TableCell>
);

interface CampaignTableProps extends ConnectedProps<typeof connector> {
  typeFilter?: string[];
}

const CampaignTable: React.FC<CampaignTableProps> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(PAGINATION_CONSTANTS.DEFAULT_ROWS_PER_PAGE);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [startDateFrom, setStartDateFrom] = useState<Date | null>(null);
  const [startDateTo, setStartDateTo] = useState<Date | null>(null);
  const [endDateFrom, setEndDateFrom] = useState<Date | null>(null);
  const [endDateTo, setEndDateTo] = useState<Date | null>(null);
  const [deletingCampaignId, setDeletingCampaignId] = useState<string | null>(null);

  const [appliedFilters, setAppliedFilters] = useState<{
    name?: string;
    startDateFrom: Date | null;
    startDateTo: Date | null;
    endDateFrom: Date | null;
    endDateTo: Date | null;
  }>({
    name: "",
    startDateFrom: null,
    startDateTo: null,
    endDateFrom: null,
    endDateTo: null,
  });

  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  const { thunkGetCampaignsExperimental, thunkCreateSuccessNotification, thunkCreateErrorNotification } = props;

  const handleNavigateToEdit = useCallback(
    (campaignNumber: string) => {
      navigate(UrlPathCampaignExperimentalEdit.replace(":campaignNumber", campaignNumber));
    },
    [navigate]
  );

  const formatDateForApi = (date: Date | null) => (date ? date.toISOString().split("T")[0] : undefined);

  const loadCampaigns = useCallback(async () => {
    const filtersPayload: {
      campaignType?: string[];
      name?: string;
      startDateFrom?: string;
      startDateTo?: string;
      endDateFrom?: string;
      endDateTo?: string;
    } = {};

    if (selectedTypes.length > 0) {
      filtersPayload.campaignType = selectedTypes;
    }
    if (appliedFilters.name) {
      filtersPayload.name = appliedFilters.name;
    }
    if (appliedFilters.startDateFrom) {
      filtersPayload.startDateFrom = formatDateForApi(appliedFilters.startDateFrom);
    }
    if (appliedFilters.startDateTo) {
      filtersPayload.startDateTo = formatDateForApi(appliedFilters.startDateTo);
    }
    if (appliedFilters.endDateFrom) {
      filtersPayload.endDateFrom = formatDateForApi(appliedFilters.endDateFrom);
    }
    if (appliedFilters.endDateTo) {
      filtersPayload.endDateTo = formatDateForApi(appliedFilters.endDateTo);
    }

    const requestParams = {
      pageNumber: page + PAGINATION_CONSTANTS.API_PAGE_OFFSET,
      pageSize: rowsPerPage,
      sortBy: sortBy || undefined,
      sortDirection: sortBy ? sortDirection : undefined,
      filters: Object.keys(filtersPayload).length > 0 ? filtersPayload : undefined,
    };

    setTableLoading(true);
    try {
      await thunkGetCampaignsExperimental(requestParams);
    } finally {
      setTableLoading(false);
      setInitialLoading(false);
    }
  }, [
    appliedFilters,
    page,
    rowsPerPage,
    selectedTypes,
    sortBy,
    sortDirection,
    thunkGetCampaignsExperimental,
  ]);

  const handleDeleteCampaign = useCallback(
    async (campaignId: string) => {
      setDeletingCampaignId(campaignId);
      try {
        await Api.deleteCampaignExperimental(campaignId);
        thunkCreateSuccessNotification(
          t("campaignExperimental.create.campaignDeletedSuccessfully", "Campaign deleted successfully")
        );
        await loadCampaigns();
      } catch (error) {
        console.error("Failed to delete campaign:", error);
        thunkCreateErrorNotification(
          t("campaignExperimental.create.campaignDeletionFailed", "Failed to delete campaign"),
          error
        );
      } finally {
        setDeletingCampaignId((current) => (current === campaignId ? null : current));
      }
    },
    [loadCampaigns, t, thunkCreateErrorNotification, thunkCreateSuccessNotification]
  );


  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      const trimmed = searchQuery.trim();
      setAppliedFilters((prev) => {
        if (prev.name === trimmed) {
          return prev;
        }
        return {
          ...prev,
          name: trimmed,
        };
      });
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchQuery]);

  useEffect(() => {
    if (startDateFrom && startDateTo && startDateTo < startDateFrom) {
      setStartDateTo(null);
    }
  }, [startDateFrom, startDateTo]);

  useEffect(() => {
    if (endDateFrom && endDateTo && endDateTo < endDateFrom) {
      setEndDateTo(null);
    }
  }, [endDateFrom, endDateTo]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      setAppliedFilters((prev) => {
        if (
          prev.startDateFrom === startDateFrom &&
          prev.startDateTo === startDateTo &&
          prev.endDateFrom === endDateFrom &&
          prev.endDateTo === endDateTo
        ) {
          return prev;
        }
        return {
          ...prev,
          startDateFrom,
          startDateTo,
          endDateFrom,
          endDateTo,
        };
      });
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [startDateFrom, startDateTo, endDateFrom, endDateTo]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDirection("asc");
    }
  };

  const handleTypeToggle = (_event: React.MouseEvent<HTMLElement>, newTypes: string[] | null) => {
    setSelectedTypes(newTypes ?? []);
    setPage(0);
  };

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper sx={{ mb: 5 }}>
        {/* Search and Filters */}
        <Box sx={{ p: 3 }}>
          {/* Search by Name */}
          <TextField
            fullWidth
            label={t("campaigns.searchByName")}
            placeholder={t("campaigns.searchPlaceholder", "Search campaigns by name...")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery("")} aria-label={t("general.clear")}>
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />

          {/* Date Range Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Start Date Range */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                {t("campaigns.startDateRange", "Start Date Range")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <DatePicker
                    label={t("general.from", "From")}
                    value={startDateFrom}
                    onChange={(date) => setStartDateFrom(date)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {startDateFrom && (
                                <IconButton
                                  size="small"
                                  aria-label={t("general.clear")}
                                  onClick={() => setStartDateFrom(null)}
                                >
                                  <Close fontSize="small" />
                                </IconButton>
                              )}
                              {params.InputProps?.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label={t("general.to", "To")}
                    value={startDateTo}
                    onChange={(date) => setStartDateTo(date)}
                    minDate={startDateFrom || undefined}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {startDateTo && (
                                <IconButton
                                  size="small"
                                  aria-label={t("general.clear")}
                                  onClick={() => setStartDateTo(null)}
                                >
                                  <Close fontSize="small" />
                                </IconButton>
                              )}
                              {params.InputProps?.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* End Date Range */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                {t("campaigns.endDateRange", "End Date Range")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <DatePicker
                    label={t("general.from", "From")}
                    value={endDateFrom}
                    onChange={(date) => setEndDateFrom(date)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {endDateFrom && (
                                <IconButton
                                  size="small"
                                  aria-label={t("general.clear")}
                                  onClick={() => setEndDateFrom(null)}
                                >
                                  <Close fontSize="small" />
                                </IconButton>
                              )}
                              {params.InputProps?.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label={t("general.to", "To")}
                    value={endDateTo}
                    onChange={(date) => setEndDateTo(date)}
                    minDate={endDateFrom || undefined}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {endDateTo && (
                                <IconButton
                                  size="small"
                                  aria-label={t("general.clear")}
                                  onClick={() => setEndDateTo(null)}
                                >
                                  <Close fontSize="small" />
                                </IconButton>
                              )}
                              {params.InputProps?.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Type Filter */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t("campaigns.filterByType", "Filter by Type")}
            </Typography>
            <ToggleButtonGroup
              value={selectedTypes}
              onChange={handleTypeToggle}
              aria-label="campaign type filter"
              size="small"
              sx={{
                "& .MuiToggleButton-root": {
                  borderRadius: "999px",
                  textTransform: "none",
                  px: 2.5,
                  borderColor: "divider",
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    borderColor: "primary.main",
                    color: "#fff",
                  },
                },
              }}
            >
              <ToggleButton value="ONE_TIME" aria-label="one time">
                {t("campaigns.oneTime", "Scheduled")}
              </ToggleButton>
              <ToggleButton value="RECURRING" aria-label="recurring">
                {t("campaigns.recurring", "Event-Driven")}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {tableLoading && (
          <Box display="flex" justifyContent="center" pb={2}>
            <CircularProgress size={24} />
          </Box>
        )}

        {/* Table */}
        <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader
                field="name"
                sx={{ minWidth: 200 }}
                onSort={handleSort}
                sortBy={sortBy}
                sortDirection={sortDirection}
              >
                {t("campaigns.name")}
              </SortableHeader>
              <SortableHeader
                field="type"
                align="center"
                onSort={handleSort}
                sortBy={sortBy}
                sortDirection={sortDirection}
              >
                {t("campaigns.type")}
              </SortableHeader>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  {t("campaigns.scheduleAndDuration")}
                </Typography>
              </TableCell>
              <SortableHeader
                field="audienceType"
                align="center"
                onSort={handleSort}
                sortBy={sortBy}
                sortDirection={sortDirection}
              >
                {t("campaigns.audience")}
              </SortableHeader>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  {t("common.actions")}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {props.campaigns.map((campaign: CampaignExperimentalDto) => (
              <CampaignRow
                key={campaign.id}
                campaign={campaign}
                onEdit={handleNavigateToEdit}
                onDelete={handleDeleteCampaign}
                isDeleting={deletingCampaignId === campaign.id}
              />
            ))}
            {props.campaigns.length === 0 && !tableLoading && (
              <TableRow>
                <TableCell colSpan={PAGINATION_CONSTANTS.TABLE_COLUMN_COUNT} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    {t("campaigns.noCampaignsFound")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={props.totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={PAGINATION_CONSTANTS.ROWS_PER_PAGE_OPTIONS}
        labelRowsPerPage={t("common.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) => {
          const displayText =
            count !== -1 ? `${from}-${to} of ${count}` : `${from}-${to} of ${t("common.moreThan")} ${to}`;
          return displayText;
        }}
      />
    </Paper>
    </LocalizationProvider>
  );
};

interface CampaignRowProps {
  campaign: CampaignExperimentalDto;
  onEdit: (campaignNumber: string) => void;
  onDelete: (campaignNumber: string) => void;
  isDeleting: boolean;
}

const CampaignTableMenu: React.FC<{
  campaign: CampaignExperimentalDto;
  onEdit: (campaignNumber: string) => void;
  onDelete: (campaignNumber: string) => void;
  isDeleting: boolean;
}> = ({ campaign, onEdit, onDelete, isDeleting }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: React.MouseEvent<HTMLElement>) => {
    event?.stopPropagation();
    setAnchorEl(null);
  };

  const handleEdit = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onEdit(campaign.id);
    handleClose();
  };

  const handleDelete = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onDelete(campaign.id);
    handleClose();
  };

  return (
    <>
      <IconButton aria-label={t("common.actions")} onClick={handleClick}>
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={handleEdit}>{t("common.edit")}</MenuItem>
        <MenuItem onClick={handleDelete} disabled={isDeleting}>
          {isDeleting
            ? t("campaignExperimental.create.deletingButton", "Deleting...")
            : t("common.delete")}
        </MenuItem>
      </Menu>
    </>
  );
};

// Helper functions for campaign data formatting
const getTypeLabel = (type: CampaignExperimentalType, t: any): string => {
  switch (type) {
    case CampaignExperimentalType.RECURRING:
      return t("campaigns.recurring");
    case CampaignExperimentalType.ONE_TIME:
      return t("campaigns.oneTime");
    default:
      return type;
  }
};

const getAudienceChip = (audienceType: string | undefined, t: any): React.ReactNode => {
  if (!audienceType) return createChip("", "default");

  const audienceConfig: Record<string, { label: string; color: ChipColor }> = {
    ALL: { label: t("campaigns.allMembers"), color: "primary" },
    CIRCLE: { label: t("campaigns.circles"), color: "warning" },
    SEGMENT: { label: t("campaigns.segments"), color: "info" },
  };

  const config = audienceConfig[audienceType];
  const label = config?.label || audienceType;
  const color = config?.color || "default";

  return createChip(label, color);
};

const getTypeChip = (type: CampaignExperimentalType, t: any): React.ReactNode => {
  const typeConfig: Record<CampaignExperimentalType, ChipColor> = {
    [CampaignExperimentalType.RECURRING]: "success",
    [CampaignExperimentalType.ONE_TIME]: "primary",
  };

  const color = typeConfig[type] || "default";
  const label = getTypeLabel(type, t);

  return createChip(label, color);
};

const getStatusLabel = (status?: string): string => status || "-";

const getStatusDotColor = (status?: CampaignExperimentalStatus): string => {
  switch (status) {
    case CampaignExperimentalStatus.ACTIVE:
      return "success.main";
    case CampaignExperimentalStatus.SCHEDULED:
      return "primary.main";
    case CampaignExperimentalStatus.ENDED:
    case CampaignExperimentalStatus.CANCELLED:
      return "error.main";
    case CampaignExperimentalStatus.COMPLETED:
      return "secondary.main";
    case CampaignExperimentalStatus.PAUSED:
      return "warning.main";
    default:
      return "text.disabled";
  }
};

// Helper functions for schedule formatting
const formatOneTimeCampaign = (campaign: CampaignExperimentalDto, isGerman: boolean): React.ReactNode => {
  const executionDate = new Date(campaign.scheduleSettings!.executionTime!);
  const formattedDate = formatCampaignDate(executionDate);
  const formattedTime = executionDate.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const text = isGerman
    ? `Läuft am ${formattedDate} um ${formattedTime}`
    : `Run at ${formattedDate} at ${formattedTime}`;

  return <Typography variant="body2">{text}</Typography>;
};

const formatRecurringCampaign = (campaign: CampaignExperimentalDto, isGerman: boolean): React.ReactNode => {
  const startDate = campaign.scheduleSettings!.startDate ? new Date(campaign.scheduleSettings!.startDate) : null;
  const endDate = campaign.scheduleSettings!.endDate ? new Date(campaign.scheduleSettings!.endDate) : null;

  if (startDate && endDate) {
    const text = isGerman
      ? `Von ${formatCampaignDate(startDate)} bis ${formatCampaignDate(endDate)}`
      : `From ${formatCampaignDate(startDate)} to ${formatCampaignDate(endDate)}`;
    return <Typography variant="body2">{text}</Typography>;
  }

  if (startDate) {
    const text = isGerman
      ? `Von ${formatCampaignDate(startDate)} laufend`
      : `From ${formatCampaignDate(startDate)} ongoing`;
    return <Typography variant="body2">{text}</Typography>;
  }

  return null;
};

const formatScheduleDuration = (campaign: CampaignExperimentalDto, isGerman: boolean): React.ReactNode => {
  // For ONE_TIME campaigns with execution time
  if (campaign.campaignType === CampaignExperimentalType.ONE_TIME && campaign.scheduleSettings?.executionTime) {
    return formatOneTimeCampaign(campaign, isGerman);
  }

  // For RECURRING campaigns with start/end dates
  if (campaign.campaignType === CampaignExperimentalType.RECURRING && campaign.scheduleSettings) {
    const recurringResult = formatRecurringCampaign(campaign, isGerman);
    if (recurringResult) {
      return recurringResult;
    }
  }

  // Fallback to original validity or dash
  return <Typography variant="body2">{campaign.validity || "-"}</Typography>;
};

const CampaignRow: React.FC<CampaignRowProps> = ({
  campaign,
  onEdit,
  onDelete,
  isDeleting,
}) => {
  const { t, i18n } = useTranslation();
  const isGerman = i18n.language === "de";

  return (
    <TableRow hover>
      <TableCell
        align="left"
        sx={{ cursor: "pointer" }}
        onClick={() => onEdit(campaign.id)}
      >
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {campaign.name}
          </Typography>
          {campaign.description && (
            <Typography variant="caption" color="text.secondary">
              {campaign.description}
            </Typography>
          )}
          <Box display="flex" alignItems="center" gap={1} mt={0.5}>
            <Box
              component="span"
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: getStatusDotColor(campaign.status),
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {getStatusLabel(campaign.status)}
            </Typography>
         </Box>
        </Box>
      </TableCell>
      <TableCell align="center">{getTypeChip(campaign.campaignType, t)}</TableCell>
      <TableCell>{formatScheduleDuration(campaign, isGerman)}</TableCell>
      <TableCell align="center">{getAudienceChip(campaign.audienceType, t)}</TableCell>
      <TableCell align="center">
        <CampaignTableMenu
          campaign={campaign}
          onEdit={onEdit}
          onDelete={onDelete}
          isDeleting={isDeleting}
        />
      </TableCell>
    </TableRow>
  );
};

// Redux connection
const mapStateToProps = (state: IStore) => ({
  campaigns: state.campaignExperimental?.campaigns?.campaigns || [],
  loading: false,
  error: null,
  totalCount: state.campaignExperimental?.campaigns?.totalCount || 0,
  totalPages: state.campaignExperimental?.campaigns?.totalPages || 0,
  currentPage: state.campaignExperimental?.campaigns?.pageNumber || 1,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  thunkGetCampaignsExperimental: (searchRequest?: any) =>
    dispatch(thunkGetAllCampaignsExperimental(searchRequest) as any),
  thunkCreateSuccessNotification: (message: string) =>
    dispatch(thunkCreateSuccessNotification(message) as any),
  thunkCreateErrorNotification: (message: string, error?: unknown) =>
    dispatch(thunkCreateErrorNotification(message, error) as any),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(CampaignTable);
